import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutlined";
import Sidebar from "../../components/common/Sidebar";
import Header from "../../components/common/Header";
import { ALL_WALKTHROUGHS, WalkthroughWithRoute } from "utils/walkthroughs";
import { fetchProjectList, getVijilization } from "services/project";

// Only "Guided Walkthroughs" is active today; the rest are placeholders.
const SECTIONS = [
  { key: "help", label: "Help", enabled: false },
  { key: "videos", label: "Videos", enabled: false },
  { key: "walkthroughs", label: "Guided Walkthroughs", enabled: true },
  { key: "tutorials", label: "Tutorials", enabled: false },
  { key: "support", label: "Support", enabled: false },
];

const getTeamId = (): string => {
  try {
    return (
      JSON.parse(localStorage.getItem("selectedTeam") as string)
        ?.currentTeamId || ""
    );
  } catch {
    return "";
  }
};

// The public, read-only demo project for guided walkthroughs, configured per
// environment (demo vs app). When set, every walkthrough link points at this
// project so the URL is identical for everyone (shareable) and works even for
// users with no team/project. Falls back to the user's first project when unset.
const PUBLIC_WALKTHROUGH_PROJECT_ID =
  process.env.REACT_APP_PUBLIC_WALKTHROUGH_PROJECT_ID || "";

// Some Help tours can't be deep-linked as their in-context version and run a
// substitute (e.g. the annotation editor tour → a single-step pointer at the
// Annotation button on the analysis page). Applied to BOTH the launch target and
// the step count shown in the list, so the displayed count matches what runs.
const HELP_WALKTHROUGH_REMAP: Record<string, string> = {
  "annotate-image": "open-annotation",
};
const effectiveWalkthrough = (w: WalkthroughWithRoute): WalkthroughWithRoute => {
  const remapId = HELP_WALKTHROUGH_REMAP[w.id];
  if (!remapId) return w;
  return ALL_WALKTHROUGHS.find((x) => x.id === remapId) || w;
};

const HelpPage = () => {
  const navigate = useNavigate();
  // Id of the walkthrough currently being resolved (fetching project/vijilize
  // context), so its button can show a loading state and block double-clicks.
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  // Resolve a context-needing walkthrough to a concrete URL by picking the
  // user's first project — and, for vijilize-scoped tours, that project's first
  // completed Vijilization — then deep-link straight there so the tour runs
  // without the user having to hunt for a project. Falls back sensibly when the
  // required context doesn't exist yet.
  const openWalkthrough = async (w: WalkthroughWithRoute) => {
    // "Annotating the Image" can't be deep-linked on its own — the annotation
    // editor needs a specific figure's data that only the analysis flow sets, so
    // a bare /annotation link bounces. Route it (via the remap) to the single-step
    // "open-annotation" tour on the analysis page, which points at the
    // "Annotation" button where the editor is actually opened.
    w = effectiveWalkthrough(w);
    // Launched from the Help section: run in the SAME tab (navigate). Only
    // in-project launches (Header) open a new tab — see handleSelectWalkthrough.
    if (!w.needsContext) {
      navigate(`${w.route}?walkthrough=${w.id}`);
      return;
    }
    if (resolvingId) return;

    setResolvingId(w.id);
    try {
      // Pick the project to run against: the public demo project when
      // configured (so the link is identical + shareable for everyone, and
      // works for users with no team), else the user's first project.
      // `summaryProjects` is only used for the fallback path to find one that
      // has a completed run.
      let projectId = PUBLIC_WALKTHROUGH_PROJECT_ID;
      let summaryProjects: any[] = [];
      if (!projectId) {
        const teamId = getTeamId();
        if (!teamId) {
          navigate("/project");
          return;
        }
        const projRes: any = await fetchProjectList(1, 50, teamId, "");
        summaryProjects = projRes?.data?.projects || [];
        if (!summaryProjects.length) {
          toast.info("Create a project first to run this walkthrough.");
          navigate("/project?walkthrough=create-project");
          return;
        }
        projectId = summaryProjects[0]._id;
      }

      const query = new URLSearchParams();

      // Vijilize-scoped tours (Reading a Vijilization, Heatmap, etc.) need an
      // actual completed run. Resolve one for the chosen project.
      if (w.route.includes(":vijilizeId")) {
        let vijilizeId = "";

        // Fallback path: prefer a project whose summary already shows a run.
        const withSummary = summaryProjects.find(
          (p) => (p.vijilizations_summary?.length || 0) > 0
        );
        if (!PUBLIC_WALKTHROUGH_PROJECT_ID && withSummary) {
          projectId = withSummary._id;
          vijilizeId = withSummary.vijilizations_summary[0]._id;
        } else {
          // Query the chosen project's runs directly (public endpoint) and pick
          // the first completed one.
          const vijRes: any = await getVijilization(projectId, "");
          const runs: any[] = Array.isArray(vijRes?.data) ? vijRes.data : [];
          const run = runs.find((r) => r.status === "completed") || runs[0];
          vijilizeId = run?._id || "";
        }

        if (!vijilizeId) {
          toast.info(
            "This tour needs a completed Vijilization. Opening the project so you can run one first."
          );
          navigate(
            `/project/${projectId}/find-covered-product?tab=comparison-library`
          );
          return;
        }

        const path = w.route
          .replace(":id", projectId)
          .replace(":vijilizeId", vijilizeId);
        query.set("walkthrough", w.id);
        navigate(`${path}?${query.toString()}`);
        return;
      }

      // Project-scoped tours (Comparison Library / Vijilizations tab). Pin the
      // sub-tab so the anchored elements are on screen.
      if (w.route.endsWith("find-covered-product")) {
        query.set(
          "tab",
          w.id === "review-vijilizations-tab"
            ? "vijilizations"
            : "comparison-library"
        );
      }
      query.set("walkthrough", w.id);
      navigate(`${w.route.replace(":id", projectId)}?${query.toString()}`);
    } catch {
      toast.error("Couldn't open that walkthrough. Please try again.");
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="dashboard-wrapper flex">
      <Sidebar />

      <div className="w-[calc(100%_-_72px)] ml-auto">
        <Header type={"settings"} />

        <div className="pt-6 px-8 h-[calc(100vh_-_70px)] overflow-y-scroll scrollbar-adjust">
          <h2 className="text-[#101828] text-3xl font-semibold pb-1">
            Help Center
          </h2>
          <p className="text-sm text-[#475467] pb-6">
            Guides and resources for using IpVijil.
          </p>

          {/* Horizontal section tabs (like the Settings page) */}
          <ul className="flex items-center flex-wrap mb-5 gap-x-4 gap-y-2">
            {SECTIONS.map((s) => (
              <li
                key={s.key}
                title={s.enabled ? "" : "Coming soon"}
                className={`flex items-center gap-x-2 text-sm font-interSemiBold rounded-md py-2 px-4 ${
                  s.enabled
                    ? "text-[#1E0AF5] bg-[#E8E6FE]"
                    : "text-[#98A2B3] cursor-not-allowed select-none"
                }`}
              >
                {s.label}
                {!s.enabled && (
                  <span className="text-[10px] uppercase tracking-wide text-[#98A2B3] border border-[#EAECF0] rounded-full px-1.5 py-0.5">
                    Soon
                  </span>
                )}
              </li>
            ))}
          </ul>

          {/* Guided walkthroughs */}
          <div className="border border-[#EAECF0] rounded-lg p-4 max-w-[1040px]">
            <h3 className="text-base font-interSemiBold text-[#101828] pb-1">
              Guided Walkthroughs
            </h3>
            <p className="text-sm text-[#475467] pb-3">
              Pick a tour — the URL becomes shareable once it opens on the right
              page.
            </p>
            <div className="flex flex-col gap-1">
              {ALL_WALKTHROUGHS.filter(
                // "open-annotation" is the single-step tour that "Annotating the
                // Image" (annotate-image) remaps to — don't list it separately.
                (w) => w.id !== "open-annotation"
              ).map((w) => {
                const isResolving = resolvingId === w.id;
                return (
                  <button
                    key={w.id}
                    onClick={() => openWalkthrough(w)}
                    disabled={!!resolvingId}
                    className="w-full flex items-start gap-x-3 px-2 py-2.5 text-left hover:bg-[#F5F8FD] rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <PlayCircleOutlineIcon
                      sx={{
                        width: 22,
                        height: 22,
                        color: "#1751D0",
                        marginTop: "1px",
                        flexShrink: 0,
                      }}
                    />
                    <div>
                      <p className="text-sm font-interSemiBold text-[#344054]">
                        {w.title}
                      </p>
                      <p className="text-xs text-[#7F7D83]">
                        {(() => {
                          // Show the step count / context of what actually runs
                          // (some tours remap, e.g. annotate-image → open-annotation).
                          const eff = effectiveWalkthrough(w);
                          if (isResolving) return "Opening…";
                          return `${eff.steps.length} ${
                            eff.steps.length === 1 ? "step" : "steps"
                          }${eff.needsContext ? " · opens in a project" : ""}`;
                        })()}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
